import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import { blogsAPI, uploadAPI } from '../services/api';

const emptyForm = {
  title: '',
  title_ar: '',
  slug: '',
  excerpt: '',
  excerpt_ar: '',
  body: '',
  body_ar: '',
  featured_image_url: '',
  meta_title: '',
  meta_description: '',
  meta_title_ar: '',
  meta_description_ar: '',
  is_published: true,
  published_at: '',
  sort_order: 0,
};

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const BlogFormPage = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(blogId);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await blogsAPI.getById(blogId);
        const b = response.data?.blog;
        if (!cancelled && b) {
          setFormData({
            title: b.title || '',
            title_ar: b.title_ar || '',
            slug: b.slug || '',
            excerpt: b.excerpt || '',
            excerpt_ar: b.excerpt_ar || '',
            body: b.body || '',
            body_ar: b.body_ar || '',
            featured_image_url: b.featured_image_url || '',
            meta_title: b.meta_title || '',
            meta_description: b.meta_description || '',
            meta_title_ar: b.meta_title_ar || '',
            meta_description_ar: b.meta_description_ar || '',
            is_published: Boolean(b.is_published),
            published_at: toDatetimeLocalValue(b.published_at),
            sort_order: b.sort_order ?? 0,
          });
        } else if (!cancelled && !b) {
          toast.error('Post not found');
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.message || error.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blogId, isEdit]);

  const handleCoverUpload = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const url = response.data?.image_url;
      if (!url) throw new Error('No image URL returned');
      setFormData((prev) => ({ ...prev, featured_image_url: url }));
      toast.success('Cover image uploaded');
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.body?.trim()) {
      toast.error('Body HTML / content is required');
      return;
    }

    const payload = {
      title: formData.title.trim(),
      title_ar: formData.title_ar?.trim() || null,
      slug: formData.slug.trim() || undefined,
      excerpt: formData.excerpt.trim() || null,
      excerpt_ar: formData.excerpt_ar?.trim() || null,
      body: formData.body,
      body_ar: formData.body_ar?.trim() ? formData.body_ar : null,
      featured_image_url: formData.featured_image_url.trim() || null,
      meta_title: formData.meta_title.trim() || null,
      meta_description: formData.meta_description.trim() || null,
      meta_title_ar: formData.meta_title_ar?.trim() || null,
      meta_description_ar: formData.meta_description_ar?.trim() || null,
      is_published: Boolean(formData.is_published),
      published_at: formData.published_at ? new Date(formData.published_at).toISOString() : null,
      sort_order: Number(formData.sort_order) || 0,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await blogsAPI.update(blogId, payload);
        toast.success('Post updated');
      } else {
        await blogsAPI.create(payload);
        toast.success('Post created');
      }
      navigate('/blogs');
    } catch (error) {
      let message = isEdit ? 'Error updating post: ' : 'Error creating post: ';
      if (error.response?.data?.errors) {
        message += Object.values(error.response.data.errors).flat().join(' ');
      } else if (error.response?.data?.message) {
        message += error.response.data.message;
      } else {
        message += error.message || 'Unknown error';
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? 'Edit blog post' : 'New blog post';

  return (
    <AdminSubpage title={title} backTo="/blogs">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-page-panel modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title (English) *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Title (Arabic)</label>
              <input
                type="text"
                dir="rtl"
                value={formData.title_ar}
                onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Slug (optional — lowercase, hyphens; auto from title if empty)</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="e.g. my-editorial-post"
              />
            </div>
            <div className="form-group">
              <label>Excerpt (English)</label>
              <textarea
                rows={3}
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Excerpt (Arabic)</label>
              <textarea
                rows={3}
                dir="rtl"
                value={formData.excerpt_ar}
                onChange={(e) => setFormData({ ...formData, excerpt_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Body (English) * (HTML allowed)</label>
              <textarea
                rows={16}
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Body (Arabic) (optional — HTML)</label>
              <textarea
                rows={16}
                dir="rtl"
                value={formData.body_ar}
                onChange={(e) => setFormData({ ...formData, body_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Featured image URL</label>
              <input
                type="text"
                value={formData.featured_image_url}
                onChange={(e) =>
                  setFormData({ ...formData, featured_image_url: e.target.value })
                }
                placeholder="https://…"
              />
              <input
                type="file"
                accept="image/*"
                style={{ marginTop: 8 }}
                disabled={uploadingCover}
                onChange={(e) => handleCoverUpload(e.target.files?.[0])}
              />
              {uploadingCover ? <span style={{ marginLeft: 8 }}>Uploading…</span> : null}
            </div>
            <div className="form-group">
              <label>Meta title (English)</label>
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Meta title (Arabic)</label>
              <input
                type="text"
                dir="rtl"
                value={formData.meta_title_ar}
                onChange={(e) => setFormData({ ...formData, meta_title_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Meta description (English)</label>
              <textarea
                rows={2}
                value={formData.meta_description}
                onChange={(e) =>
                  setFormData({ ...formData, meta_description: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Meta description (Arabic)</label>
              <textarea
                rows={2}
                dir="rtl"
                value={formData.meta_description_ar}
                onChange={(e) =>
                  setFormData({ ...formData, meta_description_ar: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) =>
                    setFormData({ ...formData, is_published: e.target.checked })
                  }
                />{' '}
                Published
              </label>
            </div>
            <div className="form-group">
              <label>Published at (optional)</label>
              <input
                type="datetime-local"
                value={formData.published_at}
                onChange={(e) =>
                  setFormData({ ...formData, published_at: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Sort order (higher first)</label>
              <input
                type="number"
                min={0}
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: e.target.value })
                }
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create post'}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default BlogFormPage;
